import {
    CreateProfileOutput,
    GetGroupsOutput,
    GetProfileOutput,
    GetQRCodeResponse,
    GroupMembership,
    InitiatePaymentInput,
    InitiatePaymentOutput,
    InviteUserInput,
    Job,
    ListGroupMembershipOutput,
    ListProfilesOutput,
    PromoOrderInput,
    RegisterPartnerInput,
    ResignFileOutput,
    SaveProfileInput,
    UploadOutput,
    UploadType
} from "./types.ts";
import dayjs from "dayjs";
import APIError from "./error.ts";
import {cleanAssetURL, profileIDFromAssetURL} from "./util.ts";
import {TokenRetriever} from "@memaro/auth";

type FetchOptions<OutputType> = RequestInit & {
    transformer?: (response: Response) => Promise<OutputType> | OutputType,
}

const defaultTransformer = async <OutputType>(response: Response) =>
    await response.json() as OutputType

export class Client {
    readonly endpoint: string
    readonly tokenRetriever: TokenRetriever

    constructor(endpoint: string, tokenRetriever: TokenRetriever) {
        this.endpoint = endpoint
        this.tokenRetriever = tokenRetriever
    }

    private async fetchJSON<OutputType>(path: string, {
        transformer = defaultTransformer<OutputType>,
        ...options
    }: FetchOptions<OutputType>): Promise<OutputType> {
        const headers = new Headers(options.headers)
        headers.append('Content-Type', 'application/json')
        headers.append('Accept', 'application/json')

        const token = await this.getToken()
        if (token) {
            headers.append('Authorization', `Bearer ${token}`)
        }

        options.headers = headers

        const response = await fetch(new URL(this.endpoint + path), options)
        if (!response.ok) {
            throw new APIError(response.status, await this.codeFromResponse(response))
        }

        return transformer(response)
    }

    private async codeFromResponse(resp: Response): Promise<string> {
        try {
            const errorResp = await resp.json() as {code: string}
            return errorResp.code
        } catch {
            return "unknown"
        }
    }

    private async getToken(): Promise<string | undefined> {
        try {
            return await this.tokenRetriever()
        } catch {
            // do nothing, omit authorization header
        }
        return undefined
    }

    private async get<ResponseType, OutputType = ResponseType>(path: string, options?: FetchOptions<OutputType>): Promise<OutputType> {
        return this.fetchJSON(path, {
            ...options,
            method: 'GET'
        })
    }

    private async post<ResponseType, OutputType = ResponseType>(path: string, body: object, options?: FetchOptions<OutputType>): Promise<OutputType> {
        return this.fetchJSON(path, {
            ...options,
            method: 'POST',
            body: JSON.stringify(body)
        })
    }

    private async put<ResponseType, OutputType = ResponseType>(path: string, body: object, options?: FetchOptions<OutputType>): Promise<OutputType> {
        return this.fetchJSON(path, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(body)
        })
    }

    private async del<ResponseType, OutputType = ResponseType>(path: string, body?: object, options?: FetchOptions<OutputType>): Promise<OutputType> {
        return this.fetchJSON(path, {
            ...options,
            method: 'DELETE',
            body: JSON.stringify(body)
        })
    }


    async uploadMulti(profileID: string, type: UploadType, files: File[]): Promise<Map<string, string>> {
        const output = await this.post<UploadOutput>(`/api/v1/profiles/${profileID}/upload`, {
            type,
            fileNames: files.map(file => file.name)
        })

        await Promise.all(output.files.map(async (outputFile): Promise<void> => {
            const f = files.find(file => file.name == outputFile.fileName)
            const uploadResponse = await fetch(outputFile.uploadURL, {
                method: 'PUT',
                headers: outputFile.uploadHeaders,
                body: f,
            })
            if (!uploadResponse.ok) {
                throw `invalid response code for upload request: ${uploadResponse.status}`
            }
        }))

        const keyValuePairs = await Promise.all(output.files.map(async (outputFile): Promise<[string, string]> => {
            for (let i = 0; i < 50; i++) {
                await new Promise(r => setTimeout(r, Math.min(1000 * Math.pow(1.2, i), 60000)));
                let job: Job | undefined
                try {
                    job = await this.getJob(outputFile.jobID)
                } catch {
                    // keep retrying
                }
                switch (job?.status) {
                    case 'succeeded': {
                        const fileName = output.files.find(file => file.jobID == outputFile.jobID)
                        if (!fileName || !job.assetURL) {
                            throw `unexpected error`
                        }
                        return [fileName.fileName, job.assetURL]
                    }
                    case 'failed':
                        throw `uploaded file could not be processed: ${job.error}`
                }
            }
            throw `uploaded file could not be processed within 30 seconds`
        }))

        return new Map(keyValuePairs)
    }

    async upload(profileID: string, type: UploadType, file: File): Promise<string> {
        const map = await this.uploadMulti(profileID, type, [file])
        return map.get(file.name)!
    }

    getJob(id: string): Promise<Job> {
        return this.get<Job>(`/api/v1/jobs/${id}`)
    }

    createProfile(): Promise<CreateProfileOutput> {
        return this.post<CreateProfileOutput>(`/api/v1/profiles`, {})
    }

    listProfiles(): Promise<ListProfilesOutput> {
        return this.get<ListProfilesOutput>(`/api/v1/profiles`)
    }

    async saveProfile(id: string, {
        etag,
        dateOfBirth,
        dateOfDeath,
        ...profile
    }: SaveProfileInput): Promise<void> {
        const options: FetchOptions<void> = {}
        if (etag) {
            options.headers = {
                "If-Match": etag
            }
        }
        return this.put<void>(`/api/v1/profiles/${id}`, {
            dateOfBirth: dateOfBirth?.format('YYYY-MM-DD'),
            dateOfDeath: dateOfDeath?.format('YYYY-MM-DD'),
            ...profile
        }, options)
    }

    async getProfile(id: string): Promise<GetProfileOutput> {
        let url = `/api/v1/profiles/${id}`
        const token = await this.getToken()
        if (!token) {
            url = `/public${url}`
        }
        return this.get<GetProfileOutput>(url, {
            transformer: async (response: Response): Promise<GetProfileOutput> => {
                const {dateOfBirth, dateOfDeath, ...data} = await response.json()
                return {
                    ...data,
                    etag: response.headers.has("etag") ? response.headers.get("etag") : undefined,
                    dateOfBirth: dateOfBirth ? dayjs(dateOfBirth, "YYYY-MM-DD") : undefined,
                    dateOfDeath: dateOfDeath ? dayjs(dateOfDeath, "YYYY-MM-DD") : undefined
                }
            },
        })
    }


    async getQRCodeURL(id: string): Promise<string> {
        for (let i = 0; i < 30; i++) {
            let response: GetQRCodeResponse | undefined
            try {
                response = await this.get<GetQRCodeResponse>(`/api/v1/profiles/${id}/qrcode`)
            } catch {
                // keep retrying
            }
            switch (response?.status) {
                case 'succeeded':
                    return response.url
                case 'failed':
                    throw 'QR-code could not be generated'
            }
            await new Promise(r => setTimeout(r, 1000));
        }
        throw `QR-code could not be generated within 30 seconds`
    }

    setPublicFlag(id: string, isPublic: boolean): Promise<void> {
        return this.post<void>(`/api/v1/profiles/${id}/public`, {
            "isPublic": isPublic,
        })
    }

    async getGroups(profileID: string): Promise<string[]> {
        if (!await this.getToken()) {
            return []
        }
        const output = await this.get<GetGroupsOutput>(`/api/v1/profiles/${profileID}/groups`)
        return output.groupIDs ?? []
    }

    listGroupMembership(profileID: string): Promise<GroupMembership[]> {
        return this.get<ListGroupMembershipOutput>(`/api/v1/profiles/${profileID}/group-membership`)
            .then(output => output.groupMemberships)
    }

    saveGroupMembership(profileID: string, gm: GroupMembership): Promise<void> {
        return this.put<void>(`/api/v1/profiles/${profileID}/group-membership`, gm)
    }

    deleteGroupMembership(profileID: string, username: string): Promise<void> {
        return this.del<void>(`/api/v1/profiles/${profileID}/group-membership`, {username})
    }


    initiatePayment(profileID: string, input: InitiatePaymentInput): Promise<InitiatePaymentOutput> {
        return this.post<InitiatePaymentOutput>(`/api/v1/profiles/${profileID}/payment/initiate`, input)
    }

    resignFile(assetURL: string): Promise<ResignFileOutput> {
        const profileID = profileIDFromAssetURL(assetURL)
        return this.post<ResignFileOutput>(`/api/v1/profiles/${profileID}/resign`, {assetURL: cleanAssetURL(assetURL)})
    }

    registerPartner(input: RegisterPartnerInput): Promise<void> {
        return this.post<void>("/public/api/v1/partners/register", input)
    }

    inviteUser(input: InviteUserInput): Promise<void> {
        return this.post<void>("/partner/api/v1/invite-user", input)
    }

    promoOrder(input: PromoOrderInput): Promise<void> {
        return this.post<void>("/partner/api/v1/promo-order", input)
    }
}


